'use client';

import type { CartShowGiftDto } from '@/api/types/gift';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType, type TrackingEvent } from '@/reporting';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

export type GiftLayerTrackingMode = 'entry' | 'popup';

export interface GiftLayerTrackingProperties {
  campaign_type_id_list: string[];
  tier_id: string;
  base_subtotal_amount: number;
  currency: string;
}

export type GiftLayerEventName = 'GiftLayer_Exp' | 'GiftLayer_Click' | 'GiftLayerPopUp_Exp';

export interface CartGiftLayerTracking {
  giftEntryRef: (node?: Element | null) => void;
  onGiftLayerClick: () => void;
}

export interface BuildCartGiftLayerEventOptions {
  eventName: GiftLayerEventName;
  showGift: CartShowGiftDto | undefined;
  currency: string;
  pageName: string;
  urlPath: string;
}

const formatCampaignTypeId = (activityId: number) => `赠品活动_商品满增_@${activityId}@`;

export function buildCartGiftLayerProperties(
  showGift: CartShowGiftDto | undefined,
  currency: string,
  mode: GiftLayerTrackingMode,
): GiftLayerTrackingProperties | null {
  const activityIds =
    mode === 'entry'
      ? [showGift?.activity_id]
      : (showGift?.detail_popup?.activities || []).map((activity) => activity.activity_id);
  const campaign_type_id_list = Array.from(
    new Set(activityIds.filter((activityId): activityId is number => activityId !== undefined)),
  ).map(formatCampaignTypeId);
  const base_subtotal_amount = Number(showGift?.base_current_amount);

  if (
    campaign_type_id_list.length === 0 ||
    showGift?.tier_id === undefined ||
    !Number.isFinite(base_subtotal_amount) ||
    !currency
  ) {
    return null;
  }

  return {
    campaign_type_id_list,
    tier_id: String(showGift.tier_id),
    base_subtotal_amount,
    currency,
  };
}

export function buildCartGiftLayerEvent({
  eventName,
  showGift,
  currency,
  pageName,
  urlPath,
}: BuildCartGiftLayerEventOptions): TrackingEvent | null {
  const mode: GiftLayerTrackingMode = eventName === 'GiftLayerPopUp_Exp' ? 'popup' : 'entry';
  const properties = buildCartGiftLayerProperties(showGift, currency, mode);

  if (!properties) return null;

  return {
    eventName,
    properties: {
      page_name: pageName,
      url_path: urlPath,
      ...properties,
    },
  };
}

export function createGiftLayerClickHandler(
  trackGiftLayerClick: () => void,
  openGiftDialog: () => void,
) {
  return () => {
    trackGiftLayerClick();
    openGiftDialog();
  };
}

export function shouldObserveGiftOfferSlide({
  index,
  activeIndex,
  rotating,
}: {
  index: number;
  activeIndex: number;
  rotating: boolean;
}) {
  return !rotating || index === activeIndex;
}

interface UseCartGiftTrackingOptions {
  showGift: CartShowGiftDto | undefined;
  currency: string;
  open: boolean;
}

export function useCartGiftTracking({
  showGift,
  currency,
  open,
}: UseCartGiftTrackingOptions): CartGiftLayerTracking {
  const { trackEvent } = useReporting();
  const { useCurrentPageName, useCurrentPathname } = useRouteInfo();
  const pageName = useCurrentPageName() || '其它';
  const urlPath =
    useCurrentPathname() || (typeof window === 'undefined' ? '' : window.location.pathname);
  const { ref: giftEntryRef, inView } = useInView({ triggerOnce: true });
  const lastExposureKeyRef = useRef<string | undefined>(undefined);
  const previousOpenRef = useRef(false);

  const track = useCallback(
    (eventName: GiftLayerEventName) => {
      const event = buildCartGiftLayerEvent({
        eventName,
        showGift,
        currency,
        pageName,
        urlPath,
      });
      if (event) trackEvent(event, [ProviderType.SENSORS]);
    },
    [currency, pageName, showGift, trackEvent, urlPath],
  );

  useEffect(() => {
    if (!inView) return;

    const properties = buildCartGiftLayerProperties(showGift, currency, 'entry');
    const exposureKey = properties ? JSON.stringify(properties) : '';
    if (!exposureKey || lastExposureKeyRef.current === exposureKey) return;

    lastExposureKeyRef.current = exposureKey;
    track('GiftLayer_Exp');
  }, [currency, inView, showGift, track]);

  useEffect(() => {
    if (open && !previousOpenRef.current) track('GiftLayerPopUp_Exp');
    previousOpenRef.current = open;
  }, [open, track]);

  return {
    giftEntryRef,
    onGiftLayerClick: () => track('GiftLayer_Click'),
  };
}
